import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Check, Delete, Edit } from '@mui/icons-material';

import { getAuthUserId } from '../../services/AuthService';
import { DoctorRoles } from '../../config/DiactiveTypes';
import { sendNewIncidentEmail } from '../../managers/MailManager';
import {
  createPatientIncident,
  deletePatientIncident,
  getPatientIncidents,
  resolveIncident,
  updatePatientIncident,
} from '../../services/IncidentService';
import { getUserFullName } from '../../services/UserService';
import { formatDateTime } from '../../utils/DiactiveUtils';
import ConfirmDeleteDialog from '../common/ConfirmDeleteDialog';
import IncidentFormDialog from './IncidentFormDialog';

const PatientIncidentsTable = ({ patientId, patientHspId, role, isDoctorPreviewMode, onDoctorPreviewBlockedAction }) => {
  const isDoctor = DoctorRoles.includes(role);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [resolveTarget, setResolveTarget] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [sortDir, setSortDir] = useState('desc');

  const toggleSort = () => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));

  const refreshItems = useCallback(async () => {
    if (!patientId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setActionError(null);
    try {
      const data = await getPatientIncidents(patientId);
      const uniqueUserIds = [...new Set([
        ...data.map((item) => item.createdBy),
        ...data.map((item) => item.resolvedBy),
      ].filter(Boolean))];
      const nameEntries = await Promise.all(
        uniqueUserIds.map(async (userId) => {
          try {
            const fullName = await getUserFullName(userId);
            return [userId, fullName];
          } catch {
            return [userId, null];
          }
        })
      );
      const namesById = Object.fromEntries(nameEntries);
      setItems(
        data.map((item) => ({
          ...item,
          createdByName: item.createdBy ? (namesById[item.createdBy] ?? null) : null,
          resolvedByName: item.resolvedBy ? (namesById[item.resolvedBy] ?? null) : null,
        }))
      );
    } catch {
      setItems([]);
      setActionError('No se han podido cargar las incidencias');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    refreshItems();
  }, [refreshItems]);

  const guardAction = (action) => {
    if (isDoctorPreviewMode) {
      onDoctorPreviewBlockedAction?.();
      return;
    }
    action();
  };

  const handleAdd = () => guardAction(() => {
    setEditingItem(null);
    setFormOpen(true);
  });

  const handleEdit = (item) => guardAction(() => {
    setEditingItem(item);
    setFormOpen(true);
  });

  const handleFormSubmit = async ({ text, createdAt }) => {
    const createdBy = getAuthUserId();
    if (editingItem) {
      await updatePatientIncident(patientId, editingItem.id, { text, createdAt });
    } else {
      await createPatientIncident(patientId, { text, createdAt, createdBy });
      await sendNewIncidentEmail(patientHspId);
    }
    await refreshItems();
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setActionError(null);
    try {
      await deletePatientIncident(patientId, deleteTarget.id);
      setDeleteTarget(null);
      await refreshItems();
    } catch (e) {
      setActionError(e.message || 'Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  const handleConfirmResolve = async () => {
    if (!resolveTarget) return;
    setResolving(true);
    setActionError(null);
    try {
      await resolveIncident(patientId, resolveTarget.id, getAuthUserId());
      setResolveTarget(null);
      await refreshItems();
    } catch (e) {
      setActionError(e.message || 'Error al resolver');
    } finally {
      setResolving(false);
    }
  };

  const renderStatusCell = (row) => {
    if (row?.status === 'resolved') {
      const tooltipTitle = (
        <Stack spacing={0.5}>
          <Typography variant="caption">
            Resuelto por: {row?.resolvedByName ?? row?.resolvedBy ?? '—'}
          </Typography>
          <Typography variant="caption">{formatDateTime(row?.resolvedAt)}</Typography>
        </Stack>
      );
      return (
        <Tooltip title={tooltipTitle} arrow>
          <Typography variant="body2" color="success.main" sx={{ width: 'fit-content' }}>
            Resuelta
          </Typography>
        </Tooltip>
      );
    }
    return <Typography variant="body2">Abierta</Typography>;
  };

  if (!patientId) {
    return null;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  const sortedItems = [...items].sort((a, b) => {
    const aMs = a.createdAt?.toDate?.()?.getTime() ?? 0;
    const bMs = b.createdAt?.toDate?.()?.getTime() ?? 0;
    return sortDir === 'asc' ? aMs - bMs : bMs - aMs;
  });

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>
          Añadir incidencia
        </Button>
      </Stack>

      {actionError && (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          {actionError}
        </Typography>
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 180, minWidth: 180 }}>
                <TableSortLabel active direction={sortDir} onClick={toggleSort}>
                  Fecha
                </TableSortLabel>
              </TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell sx={{ width: 200, minWidth: 200 }}>Creada por</TableCell>
              <TableCell sx={{ width: 140, minWidth: 140 }}>Estado</TableCell>
              {!isDoctor && (
                <TableCell sx={{ width: 140, minWidth: 140 }} align="right">
                  Acciones
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedItems.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{formatDateTime(row.createdAt)}</TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      maxWidth: 480,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {row.text}
                  </Typography>
                </TableCell>
                <TableCell>{row.createdByName ?? row.createdBy ?? '—'}</TableCell>
                <TableCell>{renderStatusCell(row)}</TableCell>
                {!isDoctor && (
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {row.status !== 'resolved' && (
                      <Tooltip title="Marcar como resuelta">
                        <IconButton
                          size="small"
                          aria-label="resolve-incident"
                          onClick={() => setResolveTarget(row)}
                        >
                          <Check color="success" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        aria-label="edit-incident"
                        onClick={() => handleEdit(row)}
                      >
                        <Edit color="primary" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton
                        size="small"
                        aria-label="delete-incident"
                        onClick={() => guardAction(() => setDeleteTarget(row))}
                      >
                        <Delete color="error" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={isDoctor ? 4 : 5}>
                  <Typography variant="body2" color="text.secondary" align="center">
                    No hay incidencias registradas para este paciente.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <IncidentFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleFormSubmit}
        title={editingItem ? 'Editar incidencia' : 'Añadir incidencia'}
        submitLabel={editingItem ? 'Guardar' : 'Añadir'}
        initialText={editingItem?.text}
        initialCreatedAt={editingItem?.createdAt}
      />

      <ConfirmDeleteDialog
        open={Boolean(deleteTarget)}
        onCancel={() => !deleting && setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title="Eliminar incidencia"
        description="¿Seguro que quieres eliminar esta incidencia? Esta acción no se puede deshacer."
        loading={deleting}
      />

      <ConfirmDeleteDialog
        open={Boolean(resolveTarget)}
        onCancel={() => !resolving && setResolveTarget(null)}
        onConfirm={handleConfirmResolve}
        title="Marcar incidencia como resuelta"
        description="¿Seguro que quieres marcar esta incidencia como resuelta?"
        confirmLabel="Resolver"
        confirmColor="primary"
        loading={resolving}
      />
    </Box>
  );
};

export default PatientIncidentsTable;
